import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Calculator, Check, Rocket, Search, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { getTax, getTaxMeta, searchAteco, updateTaxConfig } from "@/lib/api";
import { formatEUR, formatEUR0 } from "@/lib/format";

const AMBER = "#F5A623";

const pct = (v) => `${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 })}%`;
const num = (v) => parseFloat(String(v ?? "").replace(",", ".")) || 0;

// IRPEF a scaglioni (per l'anteprima live del regime ordinario).
const irpef = (taxable) => {
  let tax = 0;
  let prev = 0;
  for (const [cap, rate] of [[28000, 0.23], [50000, 0.35], [Infinity, 0.43]]) {
    if (taxable <= prev) break;
    tax += (Math.min(taxable, cap) - prev) * rate;
    prev = cap;
  }
  return tax;
};

const Row = ({ label, value, color, strong, testid }) => (
  <div className="flex items-center justify-between py-2.5" data-testid={testid}>
    <span className={`text-[13px] font-light ${strong ? "text-white/90" : "text-white/55"}`}>{label}</span>
    <span
      className={`mono tabular-nums ${strong ? "text-[15px] font-medium" : "text-[14px] font-light"}`}
      style={{ color: color || (strong ? "#fff" : "rgba(255,255,255,0.8)") }}
    >
      {value}
    </span>
  </div>
);

const NumField = ({ label, hint, value, onChange, suffix = "%", testid }) => (
  <div>
    <label className="caps-tag mb-1.5 block text-white/40">{label}</label>
    <div className="flex h-11 items-center gap-2 rounded-[8px] border border-white/10 bg-[#0D0D14] px-3 focus-within:border-[#F5A623]/50">
      <input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mono h-full w-full bg-transparent text-sm font-light tabular text-white placeholder:text-white/25 focus:outline-none"
      />
      <span className="mono text-sm text-white/40">{suffix}</span>
    </div>
    {hint && <p className="mt-1 text-[11px] font-light text-white/30">{hint}</p>}
  </div>
);

export const TaxSheet = ({ open, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  // Ricerca ATECO
  const [atecoQuery, setAtecoQuery] = useState("");
  const [atecoResults, setAtecoResults] = useState([]);
  const [atecoOpen, setAtecoOpen] = useState(false);
  const debRef = useRef(null);

  const applyConfig = (cfg) => ({
    regime: cfg.regime,
    ateco: cfg.ateco || "",
    coefficiente: cfg.coefficiente,
    startup: !!cfg.startup,
    aliquota_imposta: cfg.aliquota_imposta,
    inps_gestione: cfg.inps_gestione || "separata",
    aliquota_inps: cfg.aliquota_inps,
  });

  const load = useCallback(async () => {
    setData(null);
    try {
      const [d, m] = await Promise.all([getTax(), getTaxMeta()]);
      setData(d);
      setMeta(m);
      setForm(applyConfig(d.config));
    } catch {
      toast.error("Impossibile caricare il riepilogo fiscale");
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setEditing(false);
      setAdvanced(false);
      setAtecoQuery("");
      setAtecoOpen(false);
      load();
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Ricerca ATECO con debounce (query vuota => elenco comuni dal meta).
  useEffect(() => {
    if (!atecoOpen) return;
    if (debRef.current) clearTimeout(debRef.current);
    if (!atecoQuery.trim()) {
      setAtecoResults(meta?.ateco_comuni || []);
      return;
    }
    debRef.current = setTimeout(async () => {
      try {
        const res = await searchAteco(atecoQuery.trim());
        setAtecoResults(res || []);
      } catch {
        setAtecoResults([]);
      }
    }, 250);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [atecoQuery, atecoOpen, meta]);

  const gestioni = meta?.inps_gestioni || [];
  const gestioneObj = gestioni.find((g) => g.id === form?.inps_gestione);
  const isForf = form?.regime === "forfettario";

  const selectAteco = (r) => {
    setForm((f) => ({ ...f, ateco: r.code, coefficiente: r.coefficiente }));
    setAtecoQuery("");
    setAtecoOpen(false);
  };

  const setStartup = (v) => setForm((f) => ({ ...f, startup: v, aliquota_imposta: v ? 5 : 15 }));

  const setGestione = (id) => {
    const g = gestioni.find((x) => x.id === id);
    setForm((f) => ({ ...f, inps_gestione: id, aliquota_inps: g ? g.aliquota : f.aliquota_inps }));
  };

  // Anteprima live con i parametri correnti del form (senza salvare).
  const preview = useMemo(() => {
    if (!data || !form) return null;
    const fatt = data.fatturato_anno || 0;
    const taxable = isForf ? (fatt * num(form.coefficiente)) / 100 : fatt;
    const imposta = isForf ? (taxable * num(form.aliquota_imposta)) / 100 : irpef(taxable);
    const fisso = gestioneObj?.fisso || 0;
    const minimale = gestioneObj?.minimale || 0;
    const aliq = num(form.aliquota_inps);
    const inps = fisso > 0 ? fisso + (Math.max(0, taxable - minimale) * aliq) / 100 : (taxable * aliq) / 100;
    const total = imposta + inps;
    const eff = fatt > 0 ? (total / fatt) * 100 : 0;
    return { taxable, imposta, inps, total, eff };
  }, [data, form, isForf, gestioneObj]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        regime: form.regime,
        ateco: form.ateco || null,
        coefficiente: num(form.coefficiente),
        startup: !!form.startup,
        aliquota_imposta: num(form.aliquota_imposta),
        inps_gestione: form.inps_gestione,
        aliquota_inps: num(form.aliquota_inps),
      };
      const res = await updateTaxConfig(payload);
      setData(res.tax);
      setForm(applyConfig(res.config));
      setEditing(false);
      toast.success("Parametri fiscali aggiornati");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile salvare i parametri");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-testid="tax-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 top-12 mx-auto flex max-w-[430px] flex-col overflow-hidden rounded-t-[12px] border border-b-0 border-white/[0.08] bg-[#030305]"
          >
            {/* header terminale */}
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 pb-3 pt-3">
              <span className="mono flex items-center gap-2 text-sm font-medium tracking-[0.1em]" style={{ color: AMBER }}>
                <Calculator size={15} />
                FISCO <span className="text-white/25">//</span> LIQO
              </span>
              <button
                onClick={onClose}
                data-testid="tax-close-button"
                aria-label="Chiudi"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
              >
                <X size={14} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {data === null ? (
                <div className="space-y-4" data-testid="tax-loading">
                  <div className="h-24 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
                  <div className="h-44 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
                </div>
              ) : data.fatturato_anno <= 0 ? (
                <div data-testid="tax-empty">
                  <div className="flex flex-col items-center pt-12 text-center">
                    <span className="mono text-xl" style={{ color: AMBER }}>§</span>
                    <p className="mono mt-4 text-sm font-light text-white/85">Nessun incasso registrato quest'anno</p>
                    <p className="mt-2 max-w-[280px] text-[12px] font-light text-white/40">
                      Configura ora il tuo profilo fiscale: appena arriveranno entrate da lavoro, calcolerò quanto mettere da parte per tasse e INPS.
                    </p>
                  </div>
                  <button
                    onClick={() => setEditing(true)}
                    data-testid="tax-config-toggle"
                    className="mono mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-[6px] border border-white/[0.12] bg-transparent text-[11px] font-medium uppercase tracking-[0.18em] text-white/65 hover:bg-white/[0.04] active:scale-[0.99]"
                  >
                    <SlidersHorizontal size={13} />
                    {editing ? "Nascondi impostazioni" : "Imposta profilo fiscale"}
                  </button>
                  {editing && form && (
                    <ConfigForm
                      form={form}
                      setForm={setForm}
                      isForf={isForf}
                      gestioni={gestioni}
                      gestioneObj={gestioneObj}
                      atecoQuery={atecoQuery}
                      setAtecoQuery={setAtecoQuery}
                      atecoResults={atecoResults}
                      atecoOpen={atecoOpen}
                      setAtecoOpen={setAtecoOpen}
                      selectAteco={selectAteco}
                      setStartup={setStartup}
                      setGestione={setGestione}
                      advanced={advanced}
                      setAdvanced={setAdvanced}
                      preview={preview}
                      saving={saving}
                      onSave={save}
                    />
                  )}
                </div>
              ) : (
                <>
                  {/* hero accantonamento */}
                  <div
                    className="rounded-[12px] border bg-[#0D0D14] p-4"
                    style={{ borderColor: "rgba(245,166,35,0.25)", boxShadow: "0 0 0 1px rgba(245,166,35,0.08)" }}
                  >
                    <p className="caps-tag" style={{ color: AMBER, opacity: 0.8 }}>
                      Da accantonare · {data.anno}
                    </p>
                    <p
                      className="mono mt-2 text-[40px] font-light leading-none tracking-[-1.5px] tabular"
                      style={{ color: AMBER }}
                      data-testid="tax-total"
                    >
                      {formatEUR0(data.totale_da_accantonare)}
                    </p>
                    <p className="mono mt-3 text-[12px] tracking-[0.04em] text-white/45">
                      <span style={{ color: AMBER }}>{pct(data.percentuale_accantonamento)}</span> di ogni incasso ·
                      regime {data.regime === "forfettario" ? "Forfettario" : "Ordinario"}
                    </p>
                    {data.config?.ateco && (
                      <p className="mono mt-1 text-[11px] tracking-[0.04em] text-white/35" data-testid="tax-ateco-badge">
                        ATECO {data.config.ateco} · coeff. {pct(data.config.coefficiente)}
                      </p>
                    )}
                  </div>

                  {/* breakdown */}
                  <div className="mt-4 rounded-[12px] border border-white/[0.06] bg-[#0D0D14] px-4 py-1.5">
                    <Row label={`Fatturato ${data.anno}`} value={formatEUR(data.fatturato_anno)} testid="tax-row-fatturato" />
                    <div className="border-t border-white/[0.05]" />
                    <Row
                      label={isForf ? `Reddito imponibile (${pct(data.config.coefficiente)})` : "Reddito imponibile"}
                      value={formatEUR(data.reddito_imponibile)}
                      testid="tax-row-imponibile"
                    />
                    <div className="border-t border-white/[0.05]" />
                    <Row
                      label={isForf ? `Imposta sostitutiva (${pct(data.config.aliquota_imposta)})` : "IRPEF (a scaglioni)"}
                      value={formatEUR(data.imposta)}
                      testid="tax-row-imposta"
                    />
                    <div className="border-t border-white/[0.05]" />
                    <Row
                      label={`INPS ${gestioneLabel(data.config?.inps_gestione)} (${pct(data.config.aliquota_inps)})`}
                      value={formatEUR(data.inps)}
                      testid="tax-row-inps"
                    />
                    <div className="border-t border-white/[0.1]" />
                    <Row label="Totale da accantonare" value={formatEUR(data.totale_da_accantonare)} color={AMBER} strong testid="tax-row-totale" />
                  </div>

                  {/* questo mese */}
                  <div className="mt-3 flex gap-2">
                    <div className="flex-1 rounded-[8px] border border-white/[0.06] bg-[#0D0D14] px-3 py-2.5">
                      <p className="caps-tag text-white/35">Entrate mese</p>
                      <p className="mono mt-1 text-[15px] font-medium tabular-nums text-[#00FF87]">{formatEUR0(data.entrate_mese)}</p>
                    </div>
                    <div className="flex-1 rounded-[8px] border border-white/[0.06] bg-[#0D0D14] px-3 py-2.5" data-testid="tax-month-set-aside">
                      <p className="caps-tag text-white/35">Metti via ora</p>
                      <p className="mono mt-1 text-[15px] font-medium tabular-nums" style={{ color: AMBER }}>
                        {formatEUR0(data.da_accantonare_mese)}
                      </p>
                    </div>
                  </div>

                  {/* configurazione */}
                  <button
                    onClick={() => setEditing((v) => !v)}
                    data-testid="tax-config-toggle"
                    className="mono mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-[6px] border border-white/[0.12] bg-transparent text-[11px] font-medium uppercase tracking-[0.18em] text-white/65 hover:bg-white/[0.04] active:scale-[0.99]"
                  >
                    <SlidersHorizontal size={13} />
                    {editing ? "Nascondi impostazioni" : "Configura ATECO e regime"}
                  </button>

                  {editing && form && (
                    <ConfigForm
                      form={form}
                      setForm={setForm}
                      isForf={isForf}
                      gestioni={gestioni}
                      gestioneObj={gestioneObj}
                      atecoQuery={atecoQuery}
                      setAtecoQuery={setAtecoQuery}
                      atecoResults={atecoResults}
                      atecoOpen={atecoOpen}
                      setAtecoOpen={setAtecoOpen}
                      selectAteco={selectAteco}
                      setStartup={setStartup}
                      setGestione={setGestione}
                      advanced={advanced}
                      setAdvanced={setAdvanced}
                      preview={preview}
                      saving={saving}
                      onSave={save}
                    />
                  )}

                  <p className="mono mt-4 px-1 text-center text-[10px] leading-4 tracking-[0.05em] text-white/25">
                    Stima informativa sui flussi del conto · non sostituisce il tuo commercialista
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const GESTIONE_SHORT = {
  separata: "Gest. Separata",
  artigiani: "Artigiani",
  commercianti: "Commercianti",
  cassa: "Cassa prof.",
};
const gestioneLabel = (id) => GESTIONE_SHORT[id] || "Gest. Separata";

// ---- Form di configurazione (ATECO + regime + INPS + avanzate) ----
const ConfigForm = ({
  form,
  setForm,
  isForf,
  gestioni,
  gestioneObj,
  atecoQuery,
  setAtecoQuery,
  atecoResults,
  atecoOpen,
  setAtecoOpen,
  selectAteco,
  setStartup,
  setGestione,
  advanced,
  setAdvanced,
  preview,
  saving,
  onSave,
}) => (
  <motion.div
    initial={{ opacity: 0, height: 0 }}
    animate={{ opacity: 1, height: "auto" }}
    className="mt-3 space-y-3 overflow-hidden rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4"
    data-testid="tax-config-form"
  >
    {/* regime */}
    <div>
      <label className="caps-tag mb-1.5 block text-white/40">Regime fiscale</label>
      <Select value={form.regime} onValueChange={(v) => setForm((f) => ({ ...f, regime: v }))}>
        <SelectTrigger data-testid="tax-regime-select" className="mono h-11 rounded-[8px] border-white/10 bg-[#050508] text-sm font-light text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="border-white/10 bg-[#0D0D14] text-white">
          <SelectItem value="forfettario" data-testid="tax-regime-forfettario" className="mono text-sm focus:bg-white/[0.06]">
            Forfettario
          </SelectItem>
          <SelectItem value="ordinario" data-testid="tax-regime-ordinario" className="mono text-sm focus:bg-white/[0.06]">
            Ordinario (IRPEF)
          </SelectItem>
        </SelectContent>
      </Select>
    </div>

    {/* ATECO (solo forfettario) */}
    {isForf && (
      <div>
        <label className="caps-tag mb-1.5 block text-white/40">Codice ATECO</label>
        {form.ateco ? (
          <div
            className="flex items-center justify-between rounded-[8px] border border-[#F5A623]/30 bg-[#050508] px-3 py-2.5"
            data-testid="tax-ateco-selected"
          >
            <div className="min-w-0">
              <p className="mono text-[13px] font-medium text-white/90">{form.ateco}</p>
              <p className="mt-0.5 text-[11px] font-light text-white/40">Coefficiente redditività {pct(form.coefficiente)}</p>
            </div>
            <button
              onClick={() => { setForm((f) => ({ ...f, ateco: "" })); setAtecoOpen(true); }}
              data-testid="tax-ateco-change"
              className="mono shrink-0 rounded-[6px] border border-white/[0.12] px-2.5 py-1.5 text-[10px] uppercase tracking-[0.14em] text-white/60 hover:bg-white/[0.05]"
            >
              Cambia
            </button>
          </div>
        ) : (
          <>
            <div className="flex h-11 items-center gap-2 rounded-[8px] border border-white/10 bg-[#050508] px-3 focus-within:border-[#F5A623]/50">
              <Search size={14} className="text-white/35" />
              <input
                value={atecoQuery}
                onChange={(e) => setAtecoQuery(e.target.value)}
                onFocus={() => setAtecoOpen(true)}
                placeholder="Cerca per codice o professione…"
                data-testid="tax-ateco-search-input"
                className="mono h-full w-full bg-transparent text-sm font-light text-white placeholder:text-white/25 focus:outline-none"
              />
            </div>
            {atecoOpen && (
              <div className="mt-2 max-h-56 overflow-y-auto rounded-[8px] border border-white/[0.08] bg-[#050508]" data-testid="tax-ateco-results">
                {(atecoResults || []).length === 0 ? (
                  <p className="px-3 py-3 text-[12px] font-light text-white/35">Nessun risultato.</p>
                ) : (
                  atecoResults.map((r) => (
                    <button
                      key={r.code}
                      onClick={() => selectAteco(r)}
                      data-testid={`tax-ateco-result-${r.code}`}
                      className="flex w-full items-center justify-between gap-3 border-b border-white/[0.04] px-3 py-2.5 text-left last:border-b-0 hover:bg-white/[0.04]"
                    >
                      <div className="min-w-0">
                        <p className="mono text-[12px] font-medium text-white/85">{r.code}</p>
                        <p className="truncate text-[11px] font-light text-white/45">{r.description}</p>
                      </div>
                      <span className="mono shrink-0 text-[12px] tabular" style={{ color: AMBER }}>{pct(r.coefficiente)}</span>
                    </button>
                  ))
                )}
              </div>
            )}
            <p className="mt-1 text-[11px] font-light text-white/30">
              Dal codice ATECO deduco automaticamente il coefficiente di redditività.
            </p>
          </>
        )}
      </div>
    )}

    {/* start-up toggle (solo forfettario) */}
    {isForf && (
      <div className="flex items-center justify-between rounded-[8px] border border-white/[0.06] bg-[#050508] px-3 py-2.5">
        <div className="flex items-start gap-2">
          <Rocket size={14} className="mt-0.5 text-white/45" />
          <div>
            <p className="text-[13px] font-light text-white/85">Sono in start-up</p>
            <p className="mt-0.5 text-[11px] font-light text-white/35">Primi 5 anni · imposta al 5% (poi 15%)</p>
          </div>
        </div>
        <Switch
          checked={!!form.startup}
          onCheckedChange={setStartup}
          data-testid="tax-startup-toggle"
          className="data-[state=checked]:bg-[#F5A623]"
        />
      </div>
    )}

    {/* gestione INPS */}
    <div>
      <label className="caps-tag mb-1.5 block text-white/40">Gestione INPS</label>
      <Select value={form.inps_gestione} onValueChange={setGestione}>
        <SelectTrigger data-testid="tax-inps-gestione-select" className="mono h-11 rounded-[8px] border-white/10 bg-[#050508] text-sm font-light text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="border-white/10 bg-[#0D0D14] text-white">
          {gestioni.map((g) => (
            <SelectItem key={g.id} value={g.id} data-testid={`tax-inps-${g.id}`} className="mono text-[13px] focus:bg-white/[0.06]">
              {g.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {gestioneObj?.fisso > 0 && (
        <p className="mt-1 text-[11px] font-light text-white/30">
          Contributo fisso {formatEUR0(gestioneObj.fisso)}/anno fino a {formatEUR0(gestioneObj.minimale)} + {pct(form.aliquota_inps)} sull'eccedenza.
        </p>
      )}
    </div>

    {/* anteprima live */}
    {preview && (
      <div className="rounded-[8px] border border-[#F5A623]/25 bg-[#050508] px-3 py-3" data-testid="tax-preview">
        <div className="flex items-center justify-between">
          <span className="caps-tag" style={{ color: AMBER, opacity: 0.8 }}>Anteprima con questi parametri</span>
          <span className="mono text-[11px] text-white/40">{pct(preview.eff.toFixed ? preview.eff.toFixed(1) : preview.eff)}</span>
        </div>
        <div className="mt-2 flex items-end justify-between">
          <span className="text-[12px] font-light text-white/50">Totale da accantonare</span>
          <span className="mono text-[20px] font-light tabular" style={{ color: AMBER }} data-testid="tax-preview-total">
            {formatEUR0(preview.total)}
          </span>
        </div>
        <div className="mt-1.5 flex items-center gap-3 text-[11px] font-light text-white/40">
          <span>Imposta {formatEUR0(preview.imposta)}</span>
          <span className="text-white/15">·</span>
          <span>INPS {formatEUR0(preview.inps)}</span>
        </div>
      </div>
    )}

    {/* avanzate */}
    <button
      onClick={() => setAdvanced((v) => !v)}
      data-testid="tax-advanced-toggle"
      className="mono flex h-9 w-full items-center justify-center gap-2 rounded-[6px] border border-white/[0.08] bg-transparent text-[10px] font-medium uppercase tracking-[0.16em] text-white/45 hover:bg-white/[0.04]"
    >
      {advanced ? "Nascondi valori avanzati" : "Regola valori manualmente"}
    </button>

    {advanced && (
      <div className="space-y-3">
        {isForf && (
          <>
            <NumField
              label="Coefficiente redditività"
              hint="Impostato dal codice ATECO. Modificalo solo se sai cosa fai."
              value={form.coefficiente}
              onChange={(v) => setForm((f) => ({ ...f, coefficiente: v }))}
              testid="tax-coefficiente-input"
            />
            <NumField
              label="Imposta sostitutiva"
              hint="5% nei primi 5 anni (start-up) · 15% a regime"
              value={form.aliquota_imposta}
              onChange={(v) => setForm((f) => ({ ...f, aliquota_imposta: v }))}
              testid="tax-aliquota-input"
            />
          </>
        )}
        {!isForf && (
          <p className="rounded-[8px] border border-white/[0.06] bg-[#050508] px-3 py-2.5 text-[12px] font-light leading-5 text-white/50">
            Nel regime ordinario l'imposta è calcolata automaticamente sugli scaglioni IRPEF (23% / 35% / 43%).
          </p>
        )}
        <NumField
          label="Aliquota INPS"
          hint="Impostata dalla gestione scelta. Personalizzabile (es. cassa professionale)."
          value={form.aliquota_inps}
          onChange={(v) => setForm((f) => ({ ...f, aliquota_inps: v }))}
          testid="tax-inps-input"
        />
      </div>
    )}

    <button
      onClick={onSave}
      disabled={saving}
      data-testid="tax-save-config-button"
      className="mono mt-1 flex h-11 w-full items-center justify-center gap-2 rounded-[6px] border bg-transparent text-[11px] font-medium uppercase tracking-[0.2em] active:scale-[0.98] disabled:opacity-50"
      style={{ borderColor: "rgba(245,166,35,0.45)", color: AMBER, boxShadow: "0 0 20px rgba(245,166,35,0.08)" }}
    >
      <Check size={14} />
      {saving ? "Salvo…" : "Salva e ricalcola"}
    </button>
  </motion.div>
);
